/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Divider } from '@coursera/cds-core';

import CourseEmailPreferences from 'bundles/account-settings/components/sections/email-preferences/CourseEmailPreferences';
import CourseraEmailPreferences from 'bundles/account-settings/components/sections/email-preferences/CourseraEmailPreferences';
import GlobalEmailNotificationPreference from 'bundles/account-settings/components/sections/email-preferences/GlobalEmailNotificationPreference';
import NotificationPreferencesTOS from 'bundles/account-settings/components/sections/email-preferences/NotificationPreferencesTOS';
import PartnerEmailPreferences from 'bundles/account-settings/components/sections/email-preferences/PartnerEmailPreferences';
import type {
  CourseEmailPreference,
  GlobalChannelNotificationPreferences,
  PartnerEmailPreference,
  UpdateStatus,
} from 'bundles/account-settings/types/EmailPreferences';
import { NotificationPreferenceChannels } from 'bundles/account-settings/types/EmailPreferences';
import { composeGlobalNotificationPreferencesByChannel } from 'bundles/account-settings/utils/notificationPreferencesHelpers';

type Props = {
  coursePreferences: CourseEmailPreference[];
  partnerPreferences: PartnerEmailPreference[];
  courseraPreferences: GlobalChannelNotificationPreferences;
  courseUpdateStatus: UpdateStatus;
  partnerUpdateStatus: UpdateStatus;
  courseraUpdateStatus: UpdateStatus;
  globalNotificationsEnabled: boolean;
};

const styles = {
  section: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
  `,
};

const EmailPreferences = ({
  coursePreferences,
  partnerPreferences,
  courseUpdateStatus,
  courseraPreferences,
  courseraUpdateStatus,
  partnerUpdateStatus,
  globalNotificationsEnabled,
}: Props) => {
  const [globalOptOutEnabled, setGlobalOptOutEnabled] = useState(!globalNotificationsEnabled);

  const hasCoursePreferences = coursePreferences?.length > 0;
  const hasPartnerPreferences = partnerPreferences?.length > 0;

  const courseraEmailPreferences = React.useMemo(
    () => composeGlobalNotificationPreferencesByChannel(courseraPreferences, NotificationPreferenceChannels.EMAIL),
    [courseraPreferences]
  );

  return (
    <div css={styles.section}>
      <GlobalEmailNotificationPreference
        preference={!globalNotificationsEnabled}
        onChange={(value) => setGlobalOptOutEnabled(value)}
      />

      <CourseraEmailPreferences
        disabled={globalOptOutEnabled}
        preferences={courseraEmailPreferences}
        updateStatus={courseraUpdateStatus}
      />

      {hasCoursePreferences && (
        <>
          <Divider color="light" orientation="horizontal" />
          <CourseEmailPreferences
            disabled={globalOptOutEnabled}
            updateStatus={courseUpdateStatus}
            coursePreferences={coursePreferences}
          />
        </>
      )}

      {hasPartnerPreferences && (
        <>
          <Divider color="light" orientation="horizontal" />
          <PartnerEmailPreferences
            disabled={globalOptOutEnabled}
            updateStatus={partnerUpdateStatus}
            partnerPreferences={partnerPreferences}
          />
        </>
      )}

      <NotificationPreferencesTOS />
    </div>
  );
};

export default EmailPreferences;
